
// client/src/context/AuthContext.jsx - Contexte d'authentification
import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../api/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger l'utilisateur depuis le localStorage au démarrage
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Vérifier si le token est toujours valide
          api.defaults.headers.common['Authorization'] = `Bearer ${parsedUser.token}`;
          
          const response = await api.get('/api/users/profile');
          setUser({ ...parsedUser, ...response.data.data });
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'utilisateur:', error);
        localStorage.removeItem('user');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    loadUser();
  }, []);

  // Fonction de connexion
  const login = async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/users/login', { email, password });
      const userData = response.data.data;
      
      // Stocker l'utilisateur dans le localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Mettre à jour les headers pour les futures requêtes
      api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      
      setUser(userData);
      return userData;
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la connexion';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction d'inscription
  const register = async (name, email, password, preferences) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/api/users/register', {
        name,
        email,
        password,
        preferences,
      });
      
      const userData = response.data.data;
      
      // Stocker l'utilisateur dans le localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Mettre à jour les headers pour les futures requêtes
      api.defaults.headers.common['Authorization'] = `Bearer ${userData.token}`;
      
      setUser(userData);
      return userData;
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de l\'inscription';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Fonction de déconnexion
  const logout = () => {
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Mettre à jour le profil
  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      console.log('Updating profile with data:', profileData);
      const response = await api.put('/api/users/profile', profileData);
      const updatedUser = { ...user, ...response.data.data };
      
      // Mettre à jour le localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour du profil';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour l'abonnement
  const updateSubscription = async (subscriptionData) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.put('/api/users/subscription', subscriptionData);
      const updatedUser = {
        ...user,
        subscription: response.data.data.subscription,
      };
      
      // Mettre à jour le localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la mise à jour de l\'abonnement';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // Rafraîchir les infos utilisateur depuis l'API
  const refreshUser = async () => {
    try {
      const response = await api.get('/api/users/profile');
      const updatedUser = { ...(user || {}), ...response.data.data };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return updatedUser;
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors du chargement du profil';
      setError(message);
      throw new Error(message);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user, // Add this line
    login,
    register,
    logout,
    updateProfile,
    updateSubscription,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Create the useAuth hook
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
